import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { SR_API_ACTION_UTILITY_PARTNER_ADDRESS_CREATE_MAPPING_DEFAULT } from 'api/utilityActionTypes';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import {
  createInitialValues,
  createFormValidationFn,
  scrollToFormField,
} from 'client/helpers/formikHelpers';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrButton, { SR_BUTTON_THEME_ADOBE_YELLOW } from 'client/components/SrButton/SrButton';

import { UPProviderSearchFormInner } from 'client/components/UPProviderSearchForm/UPProviderSearchForm';

import SrText from 'client/components/SrText';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

const getFormFields = ({ formValues = {}, setFieldValue } = {}) => {
  return [
    {
      id: 'utilityProviderId',
      type: 'dom',
      properties: {
        dom:
          formValues && formValues.utilityProviderId ? (
            <SrText font="greycliff" weight="500" fontSize="16px">
              {`Provider selected: ${formValues.utilityProviderTitle}`}
            </SrText>
          ) : (
            <UPProviderSearchFormInner
              onProviderSelected={(provider) => {
                if (setFieldValue) {
                  setFieldValue('utilityProviderId', provider.id);
                  setFieldValue('utilityProviderTitle', provider.title);
                }
              }}
            />
          ),
      },
      validations: ['required'],
    },
    {
      id: 'utilityProviderTitle',
      type: 'hidden',
    },
    {
      id: 'stateCode',
      title: 'State',
      type: 'short_text',
      properties: {
        placeholder: 'E.g. TX',
      },
      validations: ['stateCode', 'required'],
    },
    {
      id: 'county',
      title: 'County',
      type: 'short_text',
      properties: {
        placeholder: 'E.g. Travis County',
      },
    },
    {
      id: 'cityName',
      title: 'City',
      type: 'short_text',
      properties: {
        placeholder: 'E.g. Austin',
      },
    },
    {
      id: 'zipCode',
      title: 'Zip',
      type: 'short_text',
      properties: {
        placeholder: 'E.g. 12345',
      },
      validations: formValues.streetDisplay ? ['required'] : [],
    },
    {
      id: 'streetDisplay',
      title: 'Street',
      type: 'short_text',
      properties: {
        placeholder: 'E.g. 1234 Main St',
      },
    },
    {
      id: 'utilityPartnerId',
      title: 'Utility Partner ID',
      type: 'short_text',
      properties: {
        placeholder: 'Optional...',
      },
    },
    {
      id: 'isExclusive',
      title: 'Exclusive Provider',
      subtitle:
        'Use this when a partner has designated this provider as exclusive. Non-exclusive providers will be hidden when an exclusive provider is available.',
      type: 'checkbox',
      properties: {
        disabled: !formValues.utilityPartnerId,
      },
    },
  ];
};

const initialFormFields = getFormFields();

const UPCreateMappingDefaultForm = ({ afterSubmit }) => {
  const dispatch = useDispatch();
  const [submitErrorMessage, setSubmitErrorMessage] = useState(null);

  const onValidateForm = (formikBag) => {
    const formValues = formikBag.values;
    const formFields = getFormFields({
      formValues,
      setFieldValue: formikBag.setFieldValue,
    });

    const formErrors = createFormValidationFn(formFields)(formValues);
    const formFieldIdsWithErrors = Object.keys(formErrors);

    if (!formFieldIdsWithErrors.length) {
      formikBag.handleSubmit();
    } else {
      formFieldIdsWithErrors.forEach((formFieldId) => {
        formikBag.setFieldTouched(formFieldId, true, true);
      });
      scrollToFormField({
        formFieldId: formFieldIdsWithErrors[0],
      });
    }
  };

  const onSubmit = (formValues) => {
    const values = {
      utilityProviderId: parseInt(formValues.utilityProviderId, 10) || null,
      utilityPartnerId: parseInt(formValues.utilityPartnerId, 10) || null,
      stateCode: formValues.stateCode ? formValues.stateCode.toUpperCase().trim() : null,
      county: formValues.county ? formValues.county.trim() : null,
      cityName: formValues.cityName.trim() || null,
      zipCode: formValues.zipCode.trim() || null,
      streetDisplay: formValues.streetDisplay.trim() || null,
      isExclusive: !!formValues.isExclusive,
    };

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_CREATE_MAPPING_DEFAULT,
      payload: values,
      onSuccess: () => {
        if (afterSubmit) {
          afterSubmit(values);
        }
      },
      onError: (error) => {
        setSubmitErrorMessage(error.message);
      },
    });
  };
  return (
    <Formik
      initialValues={createInitialValues({ formFields: initialFormFields })}
      validate={(formValues) => {
        const formFields = getFormFields({
          formValues,
        });
        return createFormValidationFn(formFields)(formValues);
      }}
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          formValues: formikBag.values,
          setFieldValue: formikBag.setFieldValue,
        });
        return (
          <Form>
            {formFields.map((formField) => (
              <SrFormikField
                key={formField.id}
                formField={formField}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            ))}
            {submitErrorMessage && <SrErrorMessage>{submitErrorMessage}</SrErrorMessage>}
            <SrButton
              onClick={() => onValidateForm(formikBag)}
              theme={SR_BUTTON_THEME_ADOBE_YELLOW}
              margin="15px 0 0"
            >
              Create
            </SrButton>
          </Form>
        );
      }}
    />
  );
};

UPCreateMappingDefaultForm.propTypes = {
  afterSubmit: PropTypes.func,
};

export default UPCreateMappingDefaultForm;
